// Universal search — the nav's search box (Cmd/Ctrl+K, or "/").
//
// Queries GET /universal_search?q=… and renders the grouped results in the
// #universalSearchModal overlay that layouts/_nav_bar renders for signed-in
// users. Plain DOM, no jQuery: the overlay is re-rendered on every Turbolinks
// visit, so nothing is cached across pages except the module state below —
// elements are looked up when the box opens.
//
// What makes it feel instant:
//   - results for a query are cached for the page's lifetime, so backspacing
//     or re-typing a query re-renders with no request;
//   - a new query keeps the previous rows on screen while its request is in
//     flight (a thin progress bar shows), so the list never flashes empty;
//   - in-flight requests are aborted and answered out of order are ignored;
//   - the empty box shows recently opened results and page shortcuts, so the
//     most common jumps need no typing at all.

(function () {
  'use strict';

  var SEARCH_URL  = '/universal_search';
  var MIN_CHARS   = 2;
  var DEBOUNCE_MS = 120;
  var MAX_RECENT  = 6;
  var CACHE_MAX   = 60;

  var IS_MAC = /Mac|iPhone|iPad|iPod/.test(navigator.platform || '');

  // Pages the box can jump to without a request. Matched client-side on the
  // label and keywords; shown as "Jump to" when the box is empty and as a
  // trailing "Pages" section when a query matches one.
  var PAGES = [
    { label: 'Dashboard',          url: '/dashboard',  keywords: 'home overview revenue' },
    { label: 'Customers',          url: '/customers',  keywords: 'customer list people subscribers' },
    { label: 'Plans',              url: '/plans',      keywords: 'plan list payment plans' },
    { label: 'Templates',          url: '/templates',  keywords: 'template list dynamic' },
    { label: 'Discounts',          url: '/discounts',  keywords: 'discount code coupon promo' },
    { label: 'Offers',             url: '/offers',     keywords: 'offer gift card', requires: 'offers' },
    { label: 'Create a new plan',  url: '/type',       keywords: 'new plan create add' },
    { label: 'Create a template',  url: '/template-type', keywords: 'new template create add' },
    { label: 'Create a discount',  url: '/discounts/new', keywords: 'new discount code coupon create add' },
    { label: 'Settings',           url: '/users/edit', keywords: 'settings account profile branding integrations email' }
  ];

  var SECTION_ORDER  = ['customer', 'plan', 'template', 'discount', 'offer', 'page'];
  var SECTION_LABELS = { customer: 'Customers', plan: 'Plans', template: 'Templates', discount: 'Discounts', offer: 'Offers', page: 'Pages', recent: 'Recent', jump: 'Jump to' };
  var MORE_LABELS    = { customer: 'customers', plan: 'plans', template: 'templates', discount: 'discounts', offer: 'offers' };

  // Feather-style 24px glyphs, one per result type.
  var ICONS = {
    customer: '<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>',
    plan:     '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>',
    template: '<rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>',
    discount: '<path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/>',
    offer:    '<polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>',
    page:     '<line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>',
    recent:   '<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>',
    search:   '<circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>'
  };

  // ── State ──────────────────────────────────────────────────────────────

  var state = {
    open: false,
    query: '',
    seq: 0,                // request sequence; a stale response is ignored
    controller: null,      // AbortController of the in-flight request
    timer: null,
    cache: {},             // query -> payload
    cacheKeys: [],         // insertion order, for eviction
    items: [],             // rendered, navigable rows in order
    activeIdx: -1,
    renderedQuery: null,   // the query the rows on screen belong to
    openOnRender: false,   // Enter was pressed before the current query rendered
    lastFocus: null
  };

  function dom() {
    var overlay = document.getElementById('universalSearchModal');
    if (!overlay) return null;
    return {
      overlay:  overlay,
      modal:    overlay.querySelector('.usearch-modal'),
      input:    document.getElementById('universalSearchInput'),
      results:  document.getElementById('universalSearchResults'),
      progress: overlay.querySelector('.usearch-progress'),
      testMode: overlay.getAttribute('data-test-mode') === 'true',
      offers:   overlay.getAttribute('data-offers') === 'true',
      scope:    overlay.getAttribute('data-scope') || 'default'
    };
  }

  // ── Open / close ────────────────────────────────────────────────────────

  function openBox() {
    var d = dom();
    if (!d || state.open) return;
    state.open = true;
    state.lastFocus = document.activeElement;
    d.overlay.style.display = 'flex';
    document.body.classList.add('usearch-open');
    d.input.value = '';
    state.query = '';
    renderHome(d);
    // Focus after the overlay is painted — focusing a display:none input is a no-op.
    window.requestAnimationFrame(function () { d.input.focus(); });
  }

  function closeBox() {
    var d = dom();
    state.open = false;
    clearTimeout(state.timer);
    abortInFlight();
    if (d) {
      d.overlay.style.display = 'none';
      setProgress(d, false);
    }
    document.body.classList.remove('usearch-open');
    if (state.lastFocus && typeof state.lastFocus.focus === 'function') {
      try { state.lastFocus.focus(); } catch (e) { /* element may be gone */ }
    }
    state.lastFocus = null;
  }

  function toggleBox() {
    state.open ? closeBox() : openBox();
  }

  function abortInFlight() {
    if (state.controller) {
      try { state.controller.abort(); } catch (e) { /* noop */ }
      state.controller = null;
    }
  }

  // ── Input ───────────────────────────────────────────────────────────────

  function onInput() {
    var d = dom();
    if (!d) return;
    var q = normalize(d.input.value);
    state.query = q;
    state.openOnRender = false;
    clearTimeout(state.timer);

    if (q.length < MIN_CHARS) {
      abortInFlight();
      setProgress(d, false);
      renderHome(d);
      return;
    }

    var key = cacheKey(d, q);
    if (state.cache.hasOwnProperty(key)) {
      abortInFlight();
      setProgress(d, false);
      renderResults(d, state.cache[key], q);
      return;
    }

    // Keep whatever is on screen; only the very first search shows a skeleton.
    if (!d.results.querySelector('.usearch-result-item')) renderSkeleton(d);
    setProgress(d, true);
    state.timer = setTimeout(function () { search(d, q); }, DEBOUNCE_MS);
  }

  function normalize(value) {
    return (value || '').replace(/\s+/g, ' ').trim();
  }

  function onKeydown(e) {
    var d = dom();
    if (!d) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      if (d.input.value) {
        d.input.value = '';
        onInput();
      } else {
        closeBox();
      }
      return;
    }

    var count = state.items.length;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (count) setActive(count ? (state.activeIdx + 1) % count : -1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (count) setActive((state.activeIdx - 1 + count) % count);
    } else if (e.key === 'Home' && count) {
      e.preventDefault();
      setActive(0);
    } else if (e.key === 'End' && count) {
      e.preventDefault();
      setActive(count - 1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // Typing and pressing Enter in one breath: the rows on screen may
      // still be the previous query's. Open the FIRST row of the query that
      // was typed, once it arrives, rather than a stale one.
      if (state.query.length >= MIN_CHARS && state.renderedQuery !== state.query) {
        state.openOnRender = true;
        return;
      }
      var el = state.items[state.activeIdx] || state.items[0];
      if (el) go(el, e.metaKey || e.ctrlKey);
    }
  }

  function setActive(idx) {
    state.activeIdx = idx;
    for (var i = 0; i < state.items.length; i++) {
      state.items[i].classList.toggle('usearch-active', i === idx);
    }
    var el = state.items[idx];
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }

  // Open a rendered row: remember it (unless it is a page or a "see more"
  // link), then navigate. A full page load, like the nav links — the list
  // pages carry inline scripts that Turbolinks visits do not re-run.
  function go(el, newTab) {
    var url = el.getAttribute('href');
    if (!url) return;
    if (el.hasAttribute('data-remember')) remember(el);
    if (newTab) {
      window.open(url, '_blank', 'noopener');
      return;
    }
    closeBox();
    window.location.href = url;
  }

  // ── Request ─────────────────────────────────────────────────────────────

  function search(d, q) {
    abortInFlight();
    var seq = ++state.seq;
    var controller = (typeof AbortController === 'function') ? new AbortController() : null;
    state.controller = controller;

    var url = SEARCH_URL + '?q=' + encodeURIComponent(q);
    fetch(url, {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      signal: controller ? controller.signal : undefined
    }).then(function (res) {
      if (res.redirected || (res.headers.get('content-type') || '').indexOf('json') === -1) {
        // Devise redirects an expired session to the sign-in page (HTML).
        throw new Error('session');
      }
      if (!res.ok) throw new Error('http ' + res.status);
      return res.json();
    }).then(function (data) {
      if (seq !== state.seq) return;           // a newer query has been sent
      state.controller = null;
      rememberCache(cacheKey(d, q), data);
      setProgress(d, false);
      if (state.query === q) renderResults(d, data, q);
    }).catch(function (err) {
      if (err && err.name === 'AbortError') return;
      if (seq !== state.seq) return;
      state.controller = null;
      setProgress(d, false);
      if (state.query !== q) return;
      if (err && err.message === 'session') {
        renderMessage(d, 'Your session has expired. <a href="/users/sign_in">Sign in again</a> to keep searching.');
      } else {
        renderMessage(d,
          'Something went wrong while searching. ' +
          '<button type="button" class="usearch-retry" data-query="' + escAttr(q) + '">Try again</button>');
      }
    });
  }

  // Keyed by account and viewing mode as well as query: the module outlives
  // Turbolinks visits, so a result cached for one account or mode must never
  // be replayed for another (a super admin switching accounts, a mode flip).
  function cacheKey(d, q) {
    return d.scope + '|' + (d.testMode ? 'test' : 'live') + '|' + q;
  }

  function rememberCache(key, data) {
    if (!state.cache.hasOwnProperty(key)) {
      state.cacheKeys.push(key);
      if (state.cacheKeys.length > CACHE_MAX) delete state.cache[state.cacheKeys.shift()];
    }
    state.cache[key] = data;
  }

  function setProgress(d, on) {
    if (d.progress) d.progress.classList.toggle('is-active', !!on);
  }

  // ── Recent results (localStorage, per account and mode) ─────────────────

  function recentKey(d) {
    return 'paythen.usearch.recent.' + d.scope + '.' + (d.testMode ? 'test' : 'live');
  }

  function readRecent(d) {
    try {
      var raw = window.localStorage.getItem(recentKey(d));
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list.filter(function (r) { return r && r.url && r.name; }) : [];
    } catch (e) {
      return [];
    }
  }

  function remember(el) {
    var d = dom();
    if (!d) return;
    var item = {
      type:     el.getAttribute('data-type') || 'plan',
      name:     el.getAttribute('data-name') || '',
      subtitle: el.getAttribute('data-subtitle') || '',
      url:      el.getAttribute('href')
    };
    // A pasted link can open a row from the other mode; keep its flag so the
    // Recent list can label it the same way the results did.
    if (el.hasAttribute('data-test')) item.test = el.getAttribute('data-test') === 'true';
    if (!item.name || item.type === 'page') return;
    try {
      var list = readRecent(d).filter(function (r) { return r.url !== item.url; });
      list.unshift(item);
      window.localStorage.setItem(recentKey(d), JSON.stringify(list.slice(0, MAX_RECENT)));
    } catch (e) { /* storage unavailable: recents are a convenience only */ }
  }

  function forgetRecent(d) {
    try { window.localStorage.removeItem(recentKey(d)); } catch (e) { /* noop */ }
  }

  // ── Rendering ───────────────────────────────────────────────────────────

  function pagesFor(d, q) {
    var words = q ? q.toLowerCase().split(' ') : [];
    return PAGES.filter(function (p) {
      if (p.requires === 'offers' && !d.offers) return false;
      if (!words.length) return true;
      var hay = (p.label + ' ' + p.keywords).toLowerCase();
      return words.every(function (w) { return hay.indexOf(w) !== -1; });
    });
  }

  function renderHome(d) {
    state.renderedQuery = '';
    state.openOnRender = false;
    var recent = readRecent(d);
    var html = '';

    if (recent.length) {
      html += sectionLabel('recent', '<button type="button" class="usearch-clear-recent">Clear</button>');
      recent.forEach(function (r) { html += resultRow(r, '', 'recent', d); });
    }

    html += sectionLabel('jump');
    html += '<div class="usearch-chips">';
    pagesFor(d, '').forEach(function (p) {
      html += '<a href="' + escAttr(p.url) + '" class="usearch-chip" data-type="page" data-name="' + escAttr(p.label) + '">' +
                escHtml(p.label) + '</a>';
    });
    html += '</div>';

    html += '<div class="usearch-hint">' +
      'Search by customer name or email, plan or template name, discount code — or paste a Paythen link.' +
      '</div>';

    d.results.innerHTML = html;
    collectItems(d);
  }

  function renderSkeleton(d) {
    var rows = '';
    for (var i = 0; i < 3; i++) {
      rows += '<div class="usearch-skeleton"><span class="usearch-skeleton-icon"></span>' +
              '<span class="usearch-skeleton-lines"><span></span><span></span></span></div>';
    }
    d.results.innerHTML = rows;
    state.items = [];
    state.activeIdx = -1;
  }

  function renderMessage(d, html) {
    d.results.innerHTML = '<div class="usearch-empty">' + html + '</div>';
    state.items = [];
    state.activeIdx = -1;
    state.renderedQuery = state.query;
    state.openOnRender = false;
  }

  function renderResults(d, data, q) {
    var results  = (data && data.results)   || [];
    var moreUrls = (data && data.more_urls) || {};
    var pages    = pagesFor(d, q);

    if (!results.length && !pages.length) {
      renderMessage(d,
        '<strong>No results for &ldquo;' + escHtml(q) + '&rdquo;</strong>' +
        '<span class="usearch-empty-sub">Try a customer&rsquo;s email, a plan name or a pasted Paythen link.' +
        (d.testMode ? ' You&rsquo;re searching test data.' : '') + '</span>');
      return;
    }

    var grouped = {};
    results.forEach(function (r) {
      var t = SECTION_LABELS[r.type] ? r.type : 'plan';
      (grouped[t] = grouped[t] || []).push(r);
    });
    if (pages.length) grouped.page = pages.map(function (p) { return { type: 'page', name: p.label, url: p.url }; });

    var html = '';
    SECTION_ORDER.forEach(function (type) {
      var rows = grouped[type];
      if (!rows || !rows.length) return;
      html += sectionLabel(type);
      rows.forEach(function (item) { html += resultRow(item, q, type, d); });
      if (moreUrls[type]) {
        html += '<a href="' + escAttr(moreUrls[type]) + '" class="usearch-result-item usearch-more-link">' +
                  icon('search') + '<span class="usearch-result-text">See all ' + (MORE_LABELS[type] || type) +
                  ' matching &ldquo;' + escHtml(q) + '&rdquo;</span>' + arrow() +
                '</a>';
      }
    });

    d.results.innerHTML = html;
    collectItems(d);
    setActive(state.items.length ? 0 : -1);
    state.renderedQuery = q;
    if (state.openOnRender) {
      state.openOnRender = false;
      if (state.items[0]) go(state.items[0], false);
    }
  }

  function sectionLabel(type, extra) {
    return '<div class="usearch-section-label"><span>' + SECTION_LABELS[type] + '</span>' + (extra || '') + '</div>';
  }

  function resultRow(item, q, kind, d) {
    var type   = item.type || 'plan';
    var pill   = '';
    var badges = '';

    if (item.status) {
      pill = '<span class="usearch-pill usearch-pill-' + escAttr(item.status_kind || 'muted') + '">' + escHtml(item.status) + '</span>';
    }
    // A direct lookup can resolve a record from the other mode; say so.
    if (d && item.type !== 'page' && typeof item.test === 'boolean' && item.test !== d.testMode) {
      badges += '<span class="usearch-pill usearch-pill-mode">' + (item.test ? 'Test mode' : 'Live') + '</span>';
    }

    var secondary = '';
    if (type === 'plan' && item.customers_url) {
      secondary = '<span class="usearch-secondary" data-href="' + escAttr(item.customers_url) + '" title="Customers on this plan">Customers</span>';
    }

    return '<a href="' + escAttr(item.url) + '" class="usearch-result-item usearch-kind-' + kind + '"' +
             ' data-type="' + escAttr(type) + '" data-name="' + escAttr(item.name || '') + '"' +
             ' data-subtitle="' + escAttr(item.subtitle || '') + '"' +
             (typeof item.test === 'boolean' ? ' data-test="' + (item.test ? 'true' : 'false') + '"' : '') +
             (type === 'page' ? '' : ' data-remember="1"') + '>' +
             icon(kind === 'recent' ? 'recent' : type) +
             '<span class="usearch-result-text">' +
               '<span class="usearch-result-name">' + highlight(item.name || '', q) + badges + '</span>' +
               (item.subtitle ? '<span class="usearch-result-sub">' + highlight(item.subtitle, q) + '</span>' : '') +
             '</span>' +
             pill + secondary + arrow() +
           '</a>';
  }

  function icon(type) {
    var glyph = ICONS[type] || ICONS.plan;
    return '<span class="usearch-result-icon usearch-icon-' + type + '">' +
             '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' + glyph + '</svg>' +
           '</span>';
  }

  function arrow() {
    return '<svg class="usearch-result-arrow" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>';
  }

  function collectItems(d) {
    state.items = Array.prototype.slice.call(d.results.querySelectorAll('.usearch-result-item, .usearch-chip'));
    state.activeIdx = -1;
  }

  // ── Text helpers ────────────────────────────────────────────────────────

  function escHtml(str) {
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function escAttr(str) {
    return escHtml(str);
  }

  function escRegex(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  // Wraps every query word found in `raw` in <mark>. Splits the RAW text on
  // the words and escapes each piece, so an entity produced by escaping can
  // never be cut in half by a match.
  function highlight(raw, q) {
    var text = String(raw);
    var words = normalize(q).split(' ').filter(Boolean);
    if (!words.length) return escHtml(text);
    var re = new RegExp('(' + words.map(escRegex).join('|') + ')', 'gi');
    return text.split(re).map(function (piece, i) {
      return i % 2 ? '<mark class="usearch-highlight">' + escHtml(piece) + '</mark>' : escHtml(piece);
    }).join('');
  }

  // ── Wiring (once per page; the overlay is looked up lazily) ─────────────

  function bind() {
    if (window.__paythenUniversalSearchBound) return;
    window.__paythenUniversalSearchBound = true;

    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!(t instanceof Element)) return;

      if (t.closest('#universalSearchTrigger')) { e.preventDefault(); openBox(); return; }
      if (!state.open) return;

      var d = dom();
      if (!d) return;

      if (t.closest('.usearch-esc-hint')) { closeBox(); return; }
      if (t === d.overlay) { closeBox(); return; }

      var retry = t.closest('.usearch-retry');
      if (retry) { renderSkeleton(d); setProgress(d, true); search(d, retry.getAttribute('data-query') || state.query); return; }

      if (t.closest('.usearch-clear-recent')) { forgetRecent(d); renderHome(d); d.input.focus(); return; }

      var secondary = t.closest('.usearch-secondary');
      if (secondary) {
        e.preventDefault();
        var row = secondary.closest('.usearch-result-item');
        if (row) remember(row);
        closeBox();
        window.location.href = secondary.getAttribute('data-href');
        return;
      }

      var item = t.closest('.usearch-result-item, .usearch-chip');
      if (item) {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) { if (item.hasAttribute('data-remember')) remember(item); return; }
        e.preventDefault();
        go(item, false);
      }
    });

    // Hover is expressed through the same active state the keyboard uses,
    // so exactly one row is ever highlighted (no CSS :hover on rows).
    document.addEventListener('mousemove', function (e) {
      if (!state.open || !(e.target instanceof Element)) return;
      var item = e.target.closest('.usearch-result-item, .usearch-chip');
      if (!item) return;
      var idx = state.items.indexOf(item);
      if (idx !== -1 && idx !== state.activeIdx) setActive(idx);
    });

    document.addEventListener('input', function (e) {
      if (e.target && e.target.id === 'universalSearchInput') onInput();
    });

    document.addEventListener('keydown', function (e) {
      var d = dom();
      if (!d) return;

      if ((e.metaKey || e.ctrlKey) && !e.altKey && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        toggleBox();
        return;
      }

      if (state.open) {
        if (e.target === d.input) { onKeydown(e); }
        else if (e.key === 'Escape') { e.preventDefault(); closeBox(); }
        else if (e.key === 'Tab') { e.preventDefault(); d.input.focus(); }
        return;
      }

      if (e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        var t = e.target;
        var tag = (t && t.tagName || '').toUpperCase();
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (t && t.isContentEditable)) return;
        if (document.querySelector('.modal.show, .modal.in')) return;   // another dialog owns the keyboard
        e.preventDefault();
        openBox();
      }
    });

    // The overlay belongs to the page that rendered it: close before Turbolinks
    // swaps the body so the "open" state cannot outlive its elements.
    document.addEventListener('turbolinks:before-render', function () { if (state.open) closeBox(); });
  }

  function decorateTrigger() {
    var hint = document.querySelector('#universalSearchTrigger .nav-search-kbd');
    if (hint) hint.textContent = IS_MAC ? '⌘K' : 'Ctrl K';
    var mod = document.querySelector('#universalSearchModal .usearch-mod-key');
    if (mod) mod.textContent = IS_MAC ? '⌘' : 'Ctrl';
  }

  bind();
  document.addEventListener('turbolinks:load', decorateTrigger);
  if (document.readyState !== 'loading') decorateTrigger();
  else document.addEventListener('DOMContentLoaded', decorateTrigger);
}());
